import { Link, useLocation } from "@tanstack/react-router"
import { APP_VERSION } from "@/client/config"
import {
  Activity,
  BarChart3,
  BookOpen,
  Calendar,
  Captions,
  Cast,
  ChevronDown,
  Clapperboard,
  Cloud,
  Cpu,
  Database,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Gauge,
  Home,
  Landmark,
  LineChart,
  LogOut,
  Radio,
  ScrollText,
  Server,
  Stethoscope,
  TrendingUp,
  UserCog,
  Users,
  Webhook,
  Wrench,
  Tv,
  RefreshCw
} from "lucide-react"
import { useAuth0 } from "@auth0/auth0-react"
import { runbookPages } from "@/client/content/runbook"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/client/components/ui/collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/client/components/ui/sidebar"
import { ThemeToggle } from "@/client/components/theme-toggle"

const dashboardItem = {
  title: "Dashboard",
  url: "/dashboard",
  icon: Home,
}

const statusPageItems = [
  {
    title: "Invintus",
    url: "https://status.invintus.com/",
  },
  {
    title: "Invintus (Admin)",
    url: "https://status.invintus.com/admin",
  },
  {
    title: "AWS",
    url: "https://status.aws.amazon.com/",
  },
  {
    title: "Backblaze",
    url: "https://status.backblaze.com/",
  },
  {
    title: "Claude",
    url: "https://status.claude.com/",
  },
  {
    title: "Cloudflare",
    url: "https://www.cloudflarestatus.com/",
  },
  {
    title: "Cloudflare Radar Outages",
    url: "https://radar.cloudflare.com/outage-center",
  },
  {
    title: "Fastly",
    url: "https://www.fastlystatus.com/",
  },
  {
    title: "GitHub",
    url: "https://www.githubstatus.com/",
  },
  {
    title: "Wowza Streaming Cloud",
    url: "https://wowza-streaming-cloud.statuspage.io/",
  },
]

const keyDateItems = [
  {
    title: "2027 Session Dates",
    url: "/key-dates/legislative-sessions",
    icon: Landmark,
  },
]

const clientItems = [
  {
    title: "Client List",
    url: "/clients",
    icon: Users,
  },
  {
    title: "OTT Manager",
    url: "/ott-manager",
    icon: Tv,
  },
]

const monitoringItems = [
  {
    title: "Live Streams",
    url: "/events/live-streams",
    icon: Radio,
  },
  {
    title: "Upcoming Streams",
    url: "/events/upcoming-streams",
    icon: Calendar,
  },
  {
    title: "Persistent Streams",
    url: "/persistent-streams",
    icon: Cast,
  },
  {
    title: "Transcode Jobs",
    url: "/transcode-jobs",
    icon: Clapperboard,
  },
  {
    title: "Live Caption Jobs",
    url: "/monitoring/caption-jobs",
    icon: Captions,
  },
  {
    title: "Webhook Logs",
    url: "/monitoring/webhook-logs",
    icon: Webhook,
  },
]

const encoderItems = [
  {
    title: "Client Encoders",
    url: "/encoders",
    icon: Cpu,
  },
  {
    title: "Encoder Triage",
    url: "/encoders/triage",
    icon: Stethoscope,
  },
]

const eventInsightsItems = [
  {
    title: "Event Reports",
    url: "/events/reports",
    icon: LineChart,
  },
  {
    title: "Yearly Trends",
    url: "/events/trends",
    icon: TrendingUp,
  },
  {
    title: "Simulcast Reports",
    url: "/events/reports/simulcast",
    icon: Radio,
  },
  {
    title: "Captioning Reports",
    url: "/captioning/reports",
    icon: Captions,
  },
]

const utilitiesItems = [
  {
    title: "Pipeline Logs",
    url: "/pipeline-logs",
    icon: ScrollText,
  },
  {
    title: "Swap User Client",
    url: "/utilities/swap-user-client",
    icon: UserCog,
  },
  {
    title: "Update Event Status",
    url: "/utilities/update-event-status",
    icon: RefreshCw,
  },
  {
    title: "Event Media Probe",
    url: "/utilities/media-probe",
    icon: FileVideo,
  },
  {
    title: "VTT Analyzer",
    url: "/utilities/vtt-analyzer",
    icon: Captions,
  },
  {
    title: "Export VTT",
    url: "/utilities/export-vtt",
    icon: FileSpreadsheet,
  },
]

const awsItems = [
  {
    title: "RDS Health",
    url: "/aws/rds-health",
    icon: Database,
  },
  {
    title: "EC2 Health",
    url: "/aws/ec2-health",
    icon: Server,
  },
]

const runbookItems = runbookPages.map((page) => ({
  title: page.title,
  url: `/runbook/${page.slug}`,
  icon: FileText,
}))

export function AppSidebar() {
  const { user, logout } = useAuth0()
  const location = useLocation()

  const isActive = (url: string) => {
    // Exact match or starts with for nested routes (e.g., /clients/123)
    return location.pathname === url || location.pathname.startsWith(url + '/')
  }

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-semibold">Admin Portal</span>
            <span className="text-xs text-muted-foreground font-mono">
              <a href="https://github.com/TVWIT/admin-portal/commits/main/" target="_blank" rel="noreferrer noopener">
                v{APP_VERSION}
              </a>
            </span>
          </div>
          <ThemeToggle />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive(dashboardItem.url)}>
                  <Link to={dashboardItem.url}>
                    <dashboardItem.icon className="h-4 w-4" />
                    <span>{dashboardItem.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <Collapsible asChild className="group/status-pages">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <Gauge className="h-4 w-4" />
                      <span>Status Pages</span>
                      <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/status-pages:rotate-180" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {statusPageItems.map((item) => (
                        <SidebarMenuSubItem key={item.url}>
                          <SidebarMenuSubButton asChild tooltip={item.title}>
                            <a href={item.url} target="_blank" rel="noopener noreferrer">
                              {/* Icon precedes the label: truncation detection targets span:last-child */}
                              <ExternalLink />
                              <span>{item.title}</span>
                            </a>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              {keyDateItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full">
                <Users className="h-4 w-4" />
                <span className="ml-2 dark:text-white light:text-black text-base">Clients</span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenuSub>
                  {clientItems.map((item) => (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={item.url}>
                          {/* <item.icon className="h-4 w-4" /> */}
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full">
                <Activity className="h-4 w-4" />
                <span className="ml-2 dark:text-white light:text-black text-base">Monitoring</span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenuSub>
                  {monitoringItems.map((item) => (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={item.url}>
                          {/* <item.icon className="h-4 w-4" /> */}
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full">
                <Cpu className="h-4 w-4" />
                <span className="ml-2 dark:text-white light:text-black text-base">Encoders</span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenuSub>
                  {encoderItems.map((item) => (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={item.url}>
                          {/* <item.icon className="h-4 w-4" /> */}
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full">
                <BarChart3 className="h-4 w-4" />
                <span className="ml-2 dark:text-white light:text-black text-base">Event Insights</span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenuSub>
                  {eventInsightsItems.map((item) => (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={item.url}>
                          {/* <item.icon className="h-4 w-4" /> */}
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full">
                <Wrench className="h-4 w-4" />
                <span className="ml-2 dark:text-white light:text-black text-base">Utilities</span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenuSub>
                  {utilitiesItems.map((item) => (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={item.url}>
                          {/* <item.icon className="h-4 w-4" /> */}
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full">
                <Cloud className="h-4 w-4" />
                <span className="ml-2 dark:text-white light:text-black text-base">AWS</span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenuSub>
                  {awsItems.map((item) => (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={item.url}>
                          {/* <item.icon className="h-4 w-4" /> */}
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible defaultOpen className="group/collapsible">
          <SidebarGroup>
            <SidebarGroupLabel asChild>
              <CollapsibleTrigger className="w-full">
                <BookOpen className="h-4 w-4" />
                <span className="ml-2 dark:text-white light:text-black text-base">Runbook</span>
                <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenuSub>
                  {runbookItems.map((item) => (
                    <SidebarMenuSubItem key={item.title}>
                      <SidebarMenuSubButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <Link to={item.url}>
                          {/* <item.icon className="h-4 w-4" /> */}
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      </SidebarContent>
      <SidebarFooter className="p-4">
        <div className="flex items-center justify-between">
          <Link to="/profile" className="flex items-center gap-2 hover:opacity-80">
            {user?.picture && (
              <img
                src={user.picture}
                alt={user.name || "User"}
                className="h-8 w-8 rounded-full"
              />
            )}
            <div className="text-sm truncate max-w-[120px]">
              {user?.name || user?.email}
            </div>
          </Link>
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="p-2 hover:bg-sidebar-accent rounded"
            title="Logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}
